import { useCallback, useMemo, useState } from "react";
import { FieldValues, UseFormReturn } from "react-hook-form";
import styled from "styled-components";
import { z } from "zod";
import { SP_BREAKPOINT } from "../../../constants/app";
import { Client } from "../../../ducks/client";
import { Group } from "../../../ducks/group";
import { System } from "../../../ducks/system";
import { User } from "../../../ducks/user";
import { formatDateTime } from "../../../utils/date";
import { Button } from "../../atoms/Button";
import { CheckboxGroup } from "../../atoms/CheckboxGroup";
import { DatePicker } from "../../atoms/DatePicker";
import { Input } from "../../atoms/Input";
import { Select, SelectOptionType } from "../../atoms/Select";
import FormTemplate from "../../templates/FormTemplate";

type Props = {
  canEditGroup: boolean;
  user: User;
  clients: Client[];
  groups: Group[];
  systems: System[];
  onCancel: () => void;
  handleSubmit: (
    firstName: string,
    lastName: string,
    email: string,
    phoneNumber: string,
    clientId: string,
    groupId: string,
    role: string,
    validFrom: string,
    validTo: string,
    systemPermissions: string[],
    onError: (message: string) => void,
  ) => void;
};

export const UserEditForm: React.FC<Props> = ({
  canEditGroup,
  user,
  systems,
  clients,
  groups,
  onCancel,
  handleSubmit,
}) => {
  const [hasEmailError, setHasEmailError] = useState<boolean>(false);
  const [hasPhoneError, setHasPhoneError] = useState<boolean>(false);
  const [emailErrorMessage, setEmailErrorMessage] = useState<string>();
  const [phoneErrorMessage, setPhoneErrorMessage] = useState<string>();
  const [selectedClientName, setSelectedClientName] = useState<string>();

  const schema = useMemo(() => {
    return z.object({
      firstName: z.string().min(1),
      lastName: z.string().min(1),
      email: z.string().min(1),
      phoneNumber: z.string().min(0),
      clientId: z.string().min(canEditGroup ? 1 : 0),
      groupId: z.string().min(canEditGroup ? 1 : 0),
      role: z.string().min(1),
      validFrom: z.string().min(1),
      validTo: z.string().min(1),
      systemPermissions: z.array(z.string()).min(1),
    });
  }, [canEditGroup]);

  const [methods, setMethods] = useState<UseFormReturn<
    FieldValues,
    unknown,
    FieldValues
  > | null>(null);

  const clientOptions: SelectOptionType[] = useMemo(() => {
    return clients.map((client, i) => ({
      id: i,
      name: client.name,
      value: client.id,
    }));
  }, [clients]);

  const defaultClientId = useMemo(() => {
    if (canEditGroup)
      return clients.find((client) => client.name === user.clientName)?.id;
    else return "";
  }, [clients, user]);

  const defaultClientName = useMemo(() => {
    return clients.find((client) => client.name === user.clientName)?.name;
  }, [clients, user]);

  const groupOptions: SelectOptionType[] = useMemo(() => {
    const clientName = selectedClientName || defaultClientName;
    return groups
      .filter((group) => group.clientName === clientName)
      .map((group, i) => ({
        id: i,
        name: group.name,
        value: group.id,
      }));
  }, [selectedClientName, groups, defaultClientName]);

  const handleClientChange = useCallback(
    (name: string, value: string) => {
      setSelectedClientName(name);
      methods?.setValue("groupId", "");
    },
    [methods],
  );

  const roleOptions: SelectOptionType[] = useMemo(() => {
    if (canEditGroup) {
      return [
        { id: 0, name: "管理者", value: "system_administrator" },
        { id: 1, name: "グループ管理者", value: "group_administrator" },
        { id: 2, name: "利用者", value: "user" },
      ];
    } else {
      return [
        { id: 1, name: "グループ管理者", value: "group_administrator" },
        { id: 2, name: "利用者", value: "user" },
      ];
    }
  }, [canEditGroup]);

  const systemPermissions = useMemo(() => {
    return systems.map((system) => ({
      name: system.name,
    }));
  }, [systems]);

  const defaultGroupId = useMemo(() => {
    if (canEditGroup)
      return groups.find((group) => group.name === user.groupName)?.id;
    else return groups[0]?.id;
  }, [groups, user, canEditGroup]);

  const defaultGroupName = useMemo(() => {
    return groups.find((group) => group.name === user.groupName)?.name;
  }, [groups, user]);

  return (
    <FormTemplate
      schema={schema}
      setMethods={setMethods}
      defaultValues={{
        firstName: user.firstName,
        lastName: user.lastName,
        email: user.email,
        phoneNumber: user.phoneNumber,
        clientId: defaultClientId,
        groupId: defaultGroupId,
        role: user.role,
        validFrom: formatDateTime(
          user.validPeriod.from,
          false,
          "YYYY/MM/DD HH:mm",
        ),
        validTo: formatDateTime(user.validPeriod.to, false, "YYYY-MM-DDTHH:mm"),
        systemPermissions: systemPermissions,
      }}
      onSubmit={({
        firstName,
        lastName,
        email,
        phoneNumber,
        clientId,
        groupId,
        role,
        validFrom,
        validTo,
        systemPermissions,
      }) =>
        handleSubmit(
          firstName,
          lastName,
          email,
          phoneNumber,
          clientId,
          groupId,
          role,
          validFrom,
          validTo,
          systemPermissions,
          (message) => {
            if (message.includes("メール")) {
              setHasEmailError(true);
              setEmailErrorMessage(message);
            } else if (message.includes("電話")) {
              setHasPhoneError(true);
              setPhoneErrorMessage(message);
            }
          },
        )
      }
    >
      <StyledContainer>
        <StyledInputContainer>
          <StyledInputRowContainer>
            <Input
              label="姓"
              name="lastName"
              maxLength={150}
              required
              fullWidth
            />
            <Input
              label="名"
              name="firstName"
              maxLength={150}
              required
              fullWidth
            />
          </StyledInputRowContainer>

          <StyledInputRowContainer>
            <Input
              label="メールアドレス"
              name="email"
              maxLength={255}
              required
              fullWidth
              hasError={hasEmailError}
              errorMessage={emailErrorMessage}
            />
            <Input
              label="電話番号"
              name="phoneNumber"
              maxLength={11}
              hasError={hasPhoneError}
              errorMessage={phoneErrorMessage}
              fullWidth
              type="number"
            />
          </StyledInputRowContainer>

          {canEditGroup && (
            <StyledInputRowContainer>
              <Select
                name="clientId"
                label="所属"
                options={clientOptions}
                onChange={handleClientChange}
                defaultValue={defaultClientName}
                width="100%"
                required
              />
              <Select
                name="groupId"
                label="グループ"
                options={groupOptions}
                onChange={() => {}}
                defaultValue={defaultGroupName}
                width="100%"
                required
              />
            </StyledInputRowContainer>
          )}

          <StyledInputRowContainer>
            <Select
              name="role"
              label="権限"
              options={roleOptions}
              onChange={() => {}}
              width="100%"
              required
              hasSearchInput={false}
            />

            <StyledInputRowContainer>
              <DatePicker
                label="有効期間"
                name="validFrom"
                required
                defaultValue={user.validPeriod.from}
              />
              <DatePicker
                label=""
                name="validTo"
                defaultValue={user.validPeriod.to}
              />
            </StyledInputRowContainer>
          </StyledInputRowContainer>
          <CheckboxGroup
            name="systemPermissions"
            options={systems.map((system) => system.name)}
            defaultSelectedOptions={user.systemPermissions}
            label="システム許可"
            onChange={() => {}}
            required
          />
        </StyledInputContainer>

        <StyledFooter>
          <Button
            text="キャンセル"
            variant="dark"
            type="button"
            onClick={onCancel}
          />
          <Button text="更新" variant="filled" type="submit" />
        </StyledFooter>
      </StyledContainer>
    </FormTemplate>
  );
};

const StyledContainer = styled.div`
  @media (max-width: ${SP_BREAKPOINT}) {
    padding: 0;
    margin-top: 24px;
  }

  padding: 0 10%;
`;

const StyledInputContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  padding-bottom: 24px;
`;

const StyledInputRowContainer = styled.div`
  @media (max-width: ${SP_BREAKPOINT}) {
    flex-direction: column;
  }

  width: 100%;
  display: flex;
  align-items: baseline;
  gap: 24px;
`;

const StyledFooter = styled.div`
  @media (max-width: ${SP_BREAKPOINT}) {
    justify-content: center;

    button {
      width: 100%;
    }
  }

  display: flex;
  justify-content: flex-end;
  gap: 16px;
  padding: 32px 0;
  border-top: 1px solid #e6e6e6;
`;
